import React, { useState } from 'react';
import { X } from 'lucide-react';
import { useGoogleLogin } from '@react-oauth/google';
import { userAuthService } from '../../services/userAuthService';
import { useToast } from '../../contexts/ToastContext';

interface FloatingLoginModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

const FloatingLoginModal: React.FC<FloatingLoginModalProps> = ({ isOpen, onClose, onSuccess }) => {
    const [loading, setLoading] = useState(false);
    const { showSuccess, showError } = useToast();

    const googleLoginHandler = useGoogleLogin({
        onSuccess: async (response) => {
            setLoading(true);
            try {
                const result = await userAuthService.googleLogin(response.access_token, 'accessToken');
                if (result.success) {
                    showSuccess('Signed in with Google!', 'Authentication');
                    onSuccess();
                } else {
                    showError(result.message || 'Google Login failed', 'Authentication Error');
                }
            } catch (err: any) {
                showError(err.message || 'Google Login failed', 'Authentication Error');
            } finally {
                setLoading(false);
            }
        },
        onError: () => showError('Google Login was cancelled or failed', 'Authentication Error')
    });

    if (!isOpen) return null;

    const handleGoogleLogin = () => {
        googleLoginHandler();
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center pt-20">
            {/* Background Blur */}
            <div
                className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity"
                onClick={onClose}
            />

            {/* Liquid Glass Modal */}
            <div className="relative animate-fade-in-up w-full max-w-sm mx-4 bg-white/60 backdrop-blur-2xl border border-white/50 rounded-[2.5rem] p-10 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.3)]">

                {/* Decorative Orbs */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-primary/20 rounded-full blur-2xl -mr-10 -mt-10 pointer-events-none" />
                <div className="absolute bottom-0 left-0 w-32 h-32 bg-blue-400/20 rounded-full blur-2xl -ml-10 -mb-10 pointer-events-none" />

                <button
                    onClick={onClose}
                    className="absolute top-6 right-6 p-2 bg-white/50 hover:bg-white text-gray-600 rounded-full transition-colors"
                >
                    <X className="w-5 h-5" />
                </button>

                <div className="relative z-10 text-center">
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Sign in Required</h2>
                    <p className="text-sm text-gray-500 mb-8 leading-relaxed">
                        Please verify your account quickly before proceeding to checkout with your premium merchandise.
                    </p>

                    <button
                        onClick={handleGoogleLogin}
                        disabled={loading}
                        className="w-full flex items-center justify-center gap-3 bg-white hover:bg-gray-50 text-gray-800 font-semibold py-3.5 px-4 rounded-2xl shadow-sm border border-gray-200 transition-all active:scale-95 disabled:opacity-70"
                    >
                        {loading ? (
                            <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                        ) : (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                            </svg>
                        )}
                        <span>Continue with Google</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default FloatingLoginModal;
